import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref, watch, toRefs } from 'vue';
import { getI18nInstance as i18n } from '@/i18n';
import { Event } from '@/models/Event';
import { Schedule, ScheduleCollection } from '@/models/Schedule.js';
import { errToStr, debounce, formRules } from '@/helpers/tools';
import { commitToast } from '@/plugins/toast';
import ScheduleEditDialog from '@/components/schedule/EditDialog.vue';
import DateTextfield from '@/components/form/DateTextfield.vue';
import BaseList from '@/components/BaseList.vue';
import TagSelect from '@/components/tag/select.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'ScheduleEdit',
  props: {
    event: null
  },
  emits: ['saved'],
  setup(__props: any, { emit }) {

const props = __props as {
    event: Event;
};


const { event } = toRefs(props);



const headers = [
    { align: 'center', sortable: false, width: 40, value: 'icon' },
    { text: i18n().t('date'), sortable: true, width: 110, value: 'start_date' },
    { text: i18n().t('time'), sortable: true, width: 50, value: 'event_time_start' },
    { text: i18n().t('opening_time_start'), sortable: true, width: 85, value: 'opening_time_start' },
    { text: i18n().t('zusatzinformationen'), sortable: true, value: 'infos' },
    { sortable: false, align: 'center', width: 95, value: 'actions' },
];

const createNewSchedule = (data: object = {}) => {
    return new Schedule({
        event_id: event.value.getId(),
        recurring_type: Schedule.RECURRING_TYPE_SINGLE,
        recur_every_n: null,
        ...data,
    });
};

const showEdit = ref(false);
const schedule = ref(createNewSchedule());
const valid = ref(true);
const loading = ref(false);
const collection = new ScheduleCollection().setRemoteSort({
    property: 'start_date',
    direction: 'asc',
});

const filters = computed(() => {
    return [
        {
            property: 'event_id',
            value: event.value.id || -1,
        },
    ];
});

watch(
    () => event?.value?.id,
    async (newId) => {
        schedule?.value?.set('event_id', newId);
    },
);

const editRecord = (record: Schedule, recurring: boolean | null = null) => {
    if (record.isPhantom() && recurring === null) {
        recurring = false;
    }
    if (recurring === true) {
        record.set('recurring_type', Schedule.RECURRING_TYPE_DAILY);
        record.set('recur_every_n', 1);
    } else if (recurring === false) {
        record.set('recurring_type', Schedule.RECURRING_TYPE_SINGLE);
        record.set('recur_every_n', null);
    }
    schedule.value = record;
    showEdit.value = true;
};

const deleteRecord = async (record: Schedule) => {
    if (record) {
        try {
            loading.value = true;
            await record?.delete();
            emit('saved');
            await reload();
        } catch (e) {
            commitToast({ message: errToStr(e), color: 'error' });
            record?.reset();
        } finally {
            loading.value = false;
        }
    }
};

const reload = async () => {
    loading.value = true;
    try {
        const params = { filter: JSON.stringify(filters.value) };
        await collection.query(params);
    } catch (e) {
        commitToast({ message: errToStr(e), color: 'error' });
    } finally {
        loading.value = false;
    }
    return collection;
};

const saveSchedule = async (record: Schedule, isEdit = true) => {
    if (!record) {
        return;
    }

    // @see 29118
    if (!isEdit && record.start_date < record.end_date) {
        record.recurring_type = Schedule.RECURRING_TYPE_DAILY;
        record.recur_every_n = 1;
    }

    try {
        await record?.save();
        emit('saved', record);
        schedule.value = createNewSchedule();
        await reload();
    } catch (e) {
        commitToast({ message: errToStr(e), color: 'error' });
    }
};

const resetSchedule = (record: Schedule) => {
    record.reset();
    schedule.value = createNewSchedule();
};

const onInput = (field: string, value: any) => {
    schedule.value.set(field, value);
};

onMounted(async () => {
    await reload();
});

return { __sfc: true,props, event, emit, headers, createNewSchedule, showEdit, schedule, valid, loading, collection, filters, editRecord, deleteRecord, reload, saveSchedule, resetSchedule, onInput, debounce, formRules, ScheduleEditDialog, DateTextfield, BaseList, TagSelect }
}

})